/**
 * Sanitize and Highlight HTML
 *
 * Content viewer utility for sanitizing and highlighting HTML content.
 * This is specifically for agent references.
 *
 * Takes raw HTML and an array of sentence strings, sanitizes the HTML,
 * and applies highlights using <mark class="ref-highlight"> tags.
 */

import sanitizeHtml from 'sanitize-html'
import { kSanitizationAllowedTags } from '@/constants/constants-ui'
import { kAgentLegalCitationLinkHrefRegex } from '@/util/agent-citations'
import htmlTokenSequenceHighlighter from '@/util/html-processing/html-token-sequence-highlighter'

/**
 * Decode HTML entities in a string using browser's native parser
 *
 * This handles all HTML entities (named and numeric) that might exist
 * in backend sentences, ensuring they match with the sanitized HTML content.
 *
 * Examples:
 * - "&quot;" → '"'
 * - "&#8220;" → '"' (smart quote)
 * - "&amp;" → '&'
 * - "&#39;" → "'"
 *
 * @param text - Text that may contain HTML entities
 * @returns Decoded text with entities converted to their character equivalents
 */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

/**
 * Sanitize and highlight HTML content
 *
 * @param htmlString - Raw HTML content from API
 * @param sentences - Array of sentence strings to highlight (from highlighted_citations[].sentences)
 * @returns Sanitized HTML with highlights applied
 */
export function sanitizeAndHighlightHtml(htmlString: string, sentences?: string[]): string {
  // Sanitize the HTML (remove dangerous tags, normalize structure)
  const sanitizedHtml = sanitizeHtml(htmlString, {
    allowedTags: kSanitizationAllowedTags,
    allowedAttributes: {
      span: ['id', 'page-number', 'pagination-id'],
    },
    transformTags: {
      pre: 'p',
      code: 'p',
    },
  })

  return applyHighlights(sanitizedHtml, sentences)
}

/**
 * Sanitize and highlight a read-only artifact's rendered markdown.
 *
 * Same strict policy as `sanitizeAndHighlightHtml`, with one artifact-specific
 * difference: an <a> whose href is a bare citation id ("ref_" + 6 alphanumerics —
 * the shape markdown rendering gives a legal citation link) survives with only its
 * href, and every other anchor is neutralized to a plain <span> (text preserved,
 * link removed). The artifact view's citation post-pass then rewrites the kept
 * anchors to reference-viewer links or unwraps them — nothing user-navigable
 * survives sanitization as-is.
 */
export function sanitizeAndHighlightArtifactHtml(htmlString: string, sentences?: string[]): string {
  const sanitizedHtml = sanitizeHtml(htmlString, {
    allowedTags: [...kSanitizationAllowedTags, 'a'],
    allowedAttributes: {
      span: ['id', 'page-number', 'pagination-id'],
      a: ['href'],
    },
    transformTags: {
      pre: 'p',
      code: 'p',
      a: (tagName: string, attribs: Record<string, string>): sanitizeHtml.Tag =>
        kAgentLegalCitationLinkHrefRegex.test(attribs.href ?? '')
          ? { tagName, attribs: { href: attribs.href } }
          : { tagName: 'span', attribs: {} as sanitizeHtml.Attributes },
    },
  })

  return applyHighlights(sanitizedHtml, sentences)
}

/**
 * Wrap the given sentences in <mark class="ref-highlight"> tags within the HTML.
 */
function applyHighlights(sanitizedHtml: string, sentences?: string[]): string {
  // If no sentences are provided, return the sanitized HTML
  if (!sentences || sentences.length === 0) {
    return sanitizedHtml
  }

  // Decode HTML entities in sentences to ensure they match with sanitized HTML
  // Backend sentences may contain entities like &quot; &#8220; that need to be decoded
  const decodedSentences = sentences.map(decodeHtmlEntities)

  // Add <mark class="ref-highlight"> tags to the sanitized HTML
  return htmlTokenSequenceHighlighter(sanitizedHtml, decodedSentences)
}
